// src/utils/constants.ts

export const API_ENDPOINTS = {
  // Auth endpoints
  AUTH: {
    LOGIN: "/api/auth/login",
    LOGOUT: "/api/auth/logout",
    REFRESH: "/api/auth/refresh",
    CHANGE_PASSWORD: "/api/auth/change-password",
    PROFILE: "/api/auth/profile",
  },

  // Property endpoints
  PROPERTIES: {
    LIST: "/api/properties",
    CREATE: "/api/properties",
    SHOW: (id: number) => `/api/properties/${id}`,
    UPDATE: (id: number) => `/api/properties/${id}`,
    DELETE: (id: number) => `/api/properties/${id}`,
    UPLOAD_PHOTOS: (id: number) => `/api/properties/${id}/photos`,
  },

  // Property Types
  PROPERTY_TYPES: {
    LIST: "/api/property-types",
    CREATE: "/api/property-types",
    DETAIL: (id: number) => `/property-types/${id}`,
    UPDATE: (id: number) => `/api/property-types/${id}`,
    DELETE: (id: number) => `/api/property-types/${id}`,
  },

  // Regions
  REGIONS: {
    LIST: "/api/regions",
    CREATE: "/api/regions",
    UPDATE: (id: number) => `/api/regions/${id}`,
    DELETE: (id: number) => `/api/regions/${id}`,
    DETAIL: (id: number) => `/regions/${id}`,
  },

  // Townships
  TOWNSHIPS: {
    LIST: "/api/townships",
    CREATE: "/api/townships",
    UPDATE: (id: number) => `/api/townships/${id}`,
    DELETE: (id: number) => `/api/townships/${id}`,
    BY_REGION: (regionId: number) => `/api/townships?region_id=${regionId}`,
  },

  CUSTOMERS: "/customers",
  CUSTOMER_DETAIL: (id: number) => `/customers/${id}`,
  CUSTOMER_CREATE: "/customers/create",
  CUSTOMER_EDIT: (id: number) => `/customers/${id}/edit`,

  // Admin Management routes
  ADMINS: "/admins",
  ADMIN_DETAIL: (id: number) => `/admins/${id}`,
  ADMIN_CREATE: "/admins/create",
  ADMIN_EDIT: (id: number) => `/admins/${id}/edit`,

  // Inquiries
  INQUIRIES: {
    LIST: "/api/inquiries",
    SHOW: (id: number) => `/api/inquiries/${id}`,
    DELETE: (id: number) => `/api/inquiries/${id}`,
  },

  BANNERS: "/banners",
  BANNER_CREATE: "/banners/create",
  BANNER_EDIT: (id: number) => `/banners/${id}/edit`,
  BANNER_DETAIL: (id: number) => `/banners/${id}`,
} as const;

export const PROPERTY_STATUSES = [
  {value: "active", label: "Active", labelMm: "လုပ်ဆောင်နေ", color: "success"},
  {value: "sold", label: "Sold", labelMm: "ရောင်းပြီး", color: "info"},
  {value: "rented", label: "Rented", labelMm: "ငှားရမ်းပြီး", color: "warning"},
  {value: "inactive", label: "Inactive", labelMm: "ပိတ်ထား", color: "gray"},
] as const;

export const LISTING_TYPES = [
  {value: "sale", label: "For Sale", labelMm: "ရောင်းမည်", color: "primary"},
  {
    value: "rent",
    label: "For Rent",
    labelMm: "ငှားရမ်းမည်",
    color: "secondary",
  },
] as const;

export const DEVELOPMENT_TYPES = [
  {value: "normal", label: "Normal", labelMm: "ပုံမှန်", color: "gray"},
  {
    value: "new",
    label: "New Development",
    labelMm: "အသစ်တည်ဆောက်",
    color: "success",
  },
] as const;

export const FINISH_STATUSES = [
  {
    value: "not_finished",
    label: "Not Finished",
    labelMm: "မပြီးသေး",
    color: "warning",
  },
  {
    value: "partly_finished",
    label: "Partly Finished",
    labelMm: "တစ်စိတ်တစ်ပိုင်းပြီး",
    color: "info",
  },
  {
    value: "totally_finished",
    label: "Totally Finished",
    labelMm: "လုံးဝပြီးပြည့်စုံ",
    color: "success",
  },
] as const;

export const USER_ROLES = [
  {value: "admin", label: "Admin"},
  {value: "user", label: "User"},
] as const;

export const PAGINATION_SIZES = [10, 25, 50, 100] as const;

export const DEFAULT_PAGINATION = {
  page: 1,
  limit: 25,
  total: 0,
} as const;

export const STORAGE_KEYS = {
  AUTH_TOKEN: "auth_token",
  USER: "auth_user",
  LANGUAGE: "app_language",
  THEME: "app_theme",
  SIDEBAR_COLLAPSED: "sidebar_collapsed",
} as const;

export const ROUTES = {
  // Auth routes
  LOGIN: "/login",
  CHANGE_PASSWORD: "/change-password",

  // Dashboard routes
  DASHBOARD: "/",

  // Property routes
  PROPERTIES: "/properties",
  PROPERTY_CREATE: "/properties/create",
  PROPERTY_EDIT: (id: number) => `/properties/${id}/edit`,
  PROPERTY_DETAIL: (id: number) => `/properties/${id}`,

  // Property Type routes
  PROPERTY_TYPES: "/property-types",
  PROPERTY_TYPE_CREATE: "/property-types/create",
  PROPERTY_TYPE_EDIT: (id: number) => `/property-types/${id}/edit`,

  // Region routes
  REGIONS: "/regions",
  REGION_CREATE: "/regions/create",
  REGION_EDIT: (id: number) => `/regions/${id}/edit`,

  // Township routes
  TOWNSHIPS: "/townships",
  TOWNSHIP_CREATE: "/townships/create",
  TOWNSHIP_EDIT: (id: number) => `/townships/${id}/edit`,

  // Customer Management routes
  CUSTOMERS: "/customers",
  CUSTOMER_DETAIL: (id: number) => `/customers/${id}`,
  CUSTOMER_CREATE: "/customers/create",
  CUSTOMER_EDIT: (id: number) => `/customers/${id}/edit`,

  // Admin Management routes
  ADMINS: "/admins",
  ADMIN_DETAIL: (id: number) => `/admins/${id}`,
  ADMIN_CREATE: "/admins/create",
  ADMIN_EDIT: (id: number) => `/admins/${id}/edit`,

  // Inquiry routes
  INQUIRIES: "/inquiries",
  INQUIRY_DETAIL: (id: number) => `/inquiries/${id}`,

  // Contact routes
  CONTACTS: "/contacts",
  CONTACT_DETAIL: (id: number) => `/contacts/${id}`,

  // Price Range routes
  PRICE_RANGES: "/price-ranges",
} as const;

export const MESSAGES = {
  SUCCESS: {
    CREATED: "Successfully created!",
    UPDATED: "Successfully updated!",
    DELETED: "Successfully deleted!",
    LOGIN: "Successfully logged in!",
    LOGOUT: "Successfully logged out!",
    PASSWORD_CHANGED: "Password successfully changed!",
  },
  ERROR: {
    GENERIC: "Something went wrong. Please try again.",
    NETWORK: "Network error. Please check your connection.",
    UNAUTHORIZED: "You are not authorized to perform this action.",
    NOT_FOUND: "The requested resource was not found.",
    VALIDATION: "Please check your input and try again.",
    LOGIN_FAILED: "Invalid email or password.",
  },
  CONFIRM: {
    DELETE: "Are you sure you want to delete this item?",
    LOGOUT: "Are you sure you want to logout?",
  },
} as const;
